import type { Currency } from "@/types/general";
import { roundTo } from "@helpers/utils";
import { isGroup, LineGroupUnion, LineSchema } from "./components/MutationLayout/types";

/**
 * Currency of a sale order (WEB-432).
 *
 * Every amount of an order (line prices, totals, contribution margin) is in the order currency,
 * while product prices (selling price, LPP, customer prices) are always in the company base
 * currency. These helpers bring base amounts into the order currency the way the backend does:
 * multiplied by the rate and, for prices, rounded to the decimals of the currency.
 */

export type OrderCurrencyDetails = Pick<
    Currency.Root,
    "id" | "name" | "code" | "symbol" | "rate" | "isBase"
>;

export interface OrderCurrency {
    currency: OrderCurrencyDetails;
    /** Units of the order currency per 1 unit of the base currency. */
    rate: number;
    /** The rate stops following today's rate once the order is picked or its prepaid invoice is sent. */
    isRateLocked: boolean;
}

/** Decimals an amount in the currency carries (EUR 2, JPY 0), from the same ICU data the backend uses. */
export function getCurrencyFractionDigits(code: string): number {
    return new Intl.NumberFormat("en", { style: "currency", currency: code }).resolvedOptions()
        .maximumFractionDigits;
}

export function roundToCurrency(amount: number, code: string): number {
    return roundTo(amount, getCurrencyFractionDigits(code));
}

/** A base currency price (selling price, customer price) as the prefill of an order line. */
export function toOrderCurrencyPrice(
    basePrice: number,
    orderCurrency: Pick<OrderCurrency, "currency" | "rate">
): number {
    return roundToCurrency(basePrice * orderCurrency.rate, orderCurrency.currency.code);
}

/**
 * A base currency cost (LPP) in the order currency. Left unrounded, so margins keep their precision.
 * A missing cost stays missing: the margin helpers count it as 0.
 */
export function toOrderCurrencyCost(
    baseCost: number | null | undefined,
    orderCurrency: Pick<OrderCurrency, "rate">
): number | null {
    return baseCost === null || baseCost === undefined ? null : baseCost * orderCurrency.rate;
}

/**
 * Re-prices the lines of an unsaved order when its currency is switched, so that every price keeps
 * its value instead of silently changing units (1000 kr must not become 1000 €).
 */
export function convertLinePrices(
    lines: LineGroupUnion[],
    from: Pick<OrderCurrencyDetails, "rate">,
    to: Pick<OrderCurrencyDetails, "rate" | "code">
): LineGroupUnion[] {
    const convertLine = (line: LineSchema): LineSchema => ({
        ...line,
        unit_price: roundToCurrency((line.unit_price * to.rate) / from.rate, to.code),
    });

    return lines.map((line) =>
        isGroup(line) ? { ...line, lines: line.lines.map(convertLine) } : convertLine(line)
    );
}
